'use client';

import React, { FC } from 'react';
import { Conditional } from '@cat-ecom/pcc-components';
import { useTranslations } from 'next-intl';
import Image from 'next/image';
import { Equipment } from 'services/equipment/types';
import {
  CatButton,
  CatGridItem,
  CatCard,
  CatHeading,
  CatTextLink
} from 'components/blocks-components';
import { PRODUCT_FALLBACK_IMAGE } from 'constants/common';
import CheckPriceButton from '../../../check-price-button/check-price-button';
import Price from 'components/price';
import CommonAvailabilityWrapper from '../../../common-availability-wrapper';
import { DealerResponse } from 'services/dealer';
import { LocationResponse } from 'services/location/types';
import CommonAvailabilityKeysIcons from '../../../common-availability-keys-icons';
import AddToCart from 'components/actions/add-to-cart/add-to-cart';
import { MergedPriceAndAvailabilityResponse } from 'services/dealerPriceAvailability/types';
import ContactYourDealerPopover from 'components/contact-your-dealer/contact-your-dealer-popover/contact-your-dealer-popover';
import { usePathMatch } from 'hooks/use-path-match';
import { HEADLESS_PRODUCT_PAGE } from 'constants/links';
import { useParams } from 'next/navigation';
import { getSeoUrl } from 'utils/links';
import FullMatchFitment from '../../../full-match-fitment';
import AlternatePartSections from '../alternate-part-sections';
import { AltPartAttribute } from 'services/product/types';
import { useSelectItemEvent } from 'hooks/analytics/use-select-item-event';
import { getFormattedSearchSelectItem } from 'components/product-comparison-grid/product-comparison-card/utils';
import { ALTERNATE_PARTS_LIST } from '../../../../constants/constants';
import { CartResponse } from 'services/cart/types';
import styles from '../alternate-parts.module.scss';

interface AlternatePartCardProps {
  product: MergedPriceAndAvailabilityResponse;
  isCoreDeposite?: boolean;
  currentSpecs: AltPartAttribute[];
  isPartSerialCompatible: boolean;
  isCatCorp: boolean;
  selectedEquipment: Equipment;
  defaultUom: string;
  locale: string;
  userStoreLocationData: LocationResponse;
  availRemoveInterimStockFlag: boolean;
  dealerInformation: DealerResponse;
  isShowSourcingDetailEnabled: boolean;
  isDeliveryEnabled: boolean;
  isRTL?: boolean;
  currencyLdFeatureFlag?: boolean;
  storeLocationId: string;
  cartDetails: CartResponse;
}

const AlternatePartCard: FC<AlternatePartCardProps> = ({
  product,
  isCoreDeposite = false,
  currentSpecs,
  isPartSerialCompatible,
  isCatCorp,
  selectedEquipment,
  defaultUom,
  locale,
  userStoreLocationData,
  availRemoveInterimStockFlag,
  dealerInformation,
  isShowSourcingDetailEnabled,
  isDeliveryEnabled,
  isRTL,
  currencyLdFeatureFlag,
  cartDetails,
  storeLocationId
}) => {
  const {
    name = '',
    partNumber = '',
    imageURL = '',
    shortDescription = '',
    isSerialCompatible = false,
    attributes = [],
    currency = '',
    indirectReplacement = false,
    reman = false,
    nonReturnable = false,
    replacementParts = [],
    enableAddToCart = false,
    type = ''
  } = product || {};

  const t = useTranslations();
  const { fireEvent: fireSelectItemEvent } = useSelectItemEvent();
  // TODO - Input Validation - Ensure that all values used from params are allowed values:
  // lang should always be a two-digit string without numbers or special characters (like 'es');
  // esite should always be a string without numbers or special characters, except for the dash (like demo-dealer).
  const params = useParams();
  const esiteUrl = getSeoUrl(params);

  const isProductDisplayPage = usePathMatch(HEADLESS_PRODUCT_PAGE);
  const productLink = isProductDisplayPage
    ? partNumber
    : `${esiteUrl}/${partNumber}`;
  const showAvailabilityWrapper =
    !isCatCorp && replacementParts?.length === 0 && enableAddToCart;

  const handleSelectItemGa = () => {
    fireSelectItemEvent({
      item: getFormattedSearchSelectItem(cartDetails?.orderHeader, product),
      partName: `${product?.partNumber}: ${product?.name}`,
      locationId: storeLocationId,
      itemSummary: product,
      isSearchPageEvent: true,
      itemListId: ALTERNATE_PARTS_LIST,
      itemListName: ALTERNATE_PARTS_LIST,
      serialNumber: '',
      fitment: ''
    });
  };

  return (
    <CatGridItem>
      <CatCard className={styles['cards']}>
        <section id="img" className={styles['img']}>
          <Image
            src={imageURL || PRODUCT_FALLBACK_IMAGE}
            className={styles['img__image']}
            alt={partNumber}
            loading="lazy"
            width={160}
            height={160}
            unoptimized
          />
        </section>
        <section id="this-item" className={`d-none ${styles['this-item']}`}>
          <CatHeading
            variant="label-sm"
            className={`${styles['this-item__heading']} text-capitalize`}
          >
            {`${t('PDP_THIS_PART')}:`}
          </CatHeading>
        </section>
        <section
          id="alternate-part-title"
          className={`mb-2 ${styles['alternate-part-title']}`}
        >
          <CatHeading
            variant="label-lg"
            level="h3"
            className={styles['alternate-part-title__heading']}
          >
            <CatTextLink
              href={partNumber}
              onClick={handleSelectItemGa}
              className={styles['alternate-part-title__link']}
            >
              {`${partNumber}: ${name}`}
            </CatTextLink>
          </CatHeading>
        </section>
        <section
          id="check-price-link"
          className={isCatCorp ? styles['catcorp-link'] : styles['link']}
        >
          {!isCatCorp ? (
            <>
              <div
                className={
                  product.coreCharges
                    ? styles['discounted-price']
                    : styles['price']
                }
              >
                {replacementParts?.length === 0 && (
                  <Price
                    price={product}
                    currency={currency}
                    isRTL={isRTL}
                    currencyLdFeatureFlag={currencyLdFeatureFlag}
                    isLeftAlignPriceTag
                  />
                )}
              </div>
              {isCoreDeposite &&
              (product.coreCharges === '' ||
                product.coreCharges === undefined) ? (
                <div className={styles['price-cover']}></div>
              ) : null}
              <Conditional
                test={enableAddToCart}
                fallback={
                  <div
                    className="py-2"
                    data-testid="component__contactDealerActions"
                  >
                    <ContactYourDealerPopover
                      dealerAddress={userStoreLocationData?.address}
                      responsiveBehavior
                      fullWidth
                    />
                  </div>
                }
              >
                {replacementParts?.length === 0 ? (
                  <div className="py-2">
                    <AddToCart
                      productPrice={product}
                      fullWidth={true}
                      productPriceData={product}
                      cartId={cartDetails?.orderHeader?.orderId}
                      locationId={storeLocationId}
                      currency={currency}
                      itemlistIdName={ALTERNATE_PARTS_LIST}
                    />
                  </div>
                ) : (
                  <CatButton
                    className="py-2"
                    size="sm"
                    variant="contrast"
                    fullWidth
                    href={productLink}
                  >
                    {t('REPLACEMENT_BUTTON')}
                  </CatButton>
                )}
              </Conditional>
            </>
          ) : (
            <div className={`${styles['check-price-btn']} d-none my-2`}>
              <CheckPriceButton />
            </div>
          )}
          <div
            className={`d-flex justify-content-end ${styles['alternate-icon']}`}
          >
            <CommonAvailabilityKeysIcons
              indirectReplacement={indirectReplacement}
              reman={reman}
              nonReturnable={nonReturnable}
              type={type}
              isCatCorp={isCatCorp}
            />
          </div>
          {showAvailabilityWrapper ? (
            <CommonAvailabilityWrapper
              availabilityData={product}
              shouldDisplayLabel={false}
              isLoading={false}
              locale={locale}
              dealerData={userStoreLocationData}
              dealerInformation={dealerInformation}
              availRemoveInterimStockFlag={availRemoveInterimStockFlag}
              isShowSourcingDetailEnabled={isShowSourcingDetailEnabled}
              isDeliveryEnabled={isDeliveryEnabled}
            />
          ) : (
            // For card alignment if there is no availaibility section
            <div style={{ height: '20px' }}></div>
          )}
        </section>
        {!!selectedEquipment && isPartSerialCompatible && (
          <section
            id="compatibility"
            className={`d-flex align-items-center ${styles['section-with-background']} ${styles['compatibility']}`}
          >
            {isSerialCompatible && (
              <FullMatchFitment selectedEquipment={selectedEquipment} />
            )}
          </section>
        )}
        <AlternatePartSections
          attributes={attributes}
          currentSpecs={currentSpecs}
          shortDescription={shortDescription}
          defaultUom={defaultUom}
        />
      </CatCard>
    </CatGridItem>
  );
};

export default AlternatePartCard;
